import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Edit, Trash2, ArrowRightLeft, AlertCircle, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Assets = () => {
  const [assets, setAssets] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [filters, setFilters] = useState({
    store_id: 'all',
    category: 'all',
    condition: 'all',
    search: ''
  });
  const [editForm, setEditForm] = useState({});
  const [transferForm, setTransferForm] = useState({
    new_store_id: '',
    transfer_reason: ''
  });

  useEffect(() => {
    fetchStores();
    fetchCategories();
    fetchAssets();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [filters.store_id, filters.category, filters.condition]);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      console.error('Error al cargar tiendas');
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/asset-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error al cargar categorías');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      toast.error('Por favor ingresa un nombre para la categoría');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/asset-categories`, {
        name: newCategoryName.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Categoría creada exitosamente');
      setNewCategoryName('');
      setCategoryDialogOpen(false);
      fetchCategories(); // Recargar categorías
    } catch (error) {
      toast.error('Error al crear categoría');
    }
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = {};
      if (filters.store_id !== 'all') params.store_id = filters.store_id;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.condition !== 'all') params.condition = filters.condition;
      
      const response = await axios.get(`${API}/assets`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      toast.error('Error al cargar activos');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (asset) => {
    setSelectedAsset(asset);
    setDetailDialogOpen(true);
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setEditForm({
      name: asset.name,
      brand: asset.brand || '',
      quantity: asset.quantity,
      serial_number: asset.serial_number || '',
      condition: asset.condition,
      physical_location: asset.physical_location || '',
      notes: asset.notes || '',
      current_value: asset.current_value
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/assets/${selectedAsset.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Activo actualizado correctamente');
      setEditDialogOpen(false);
      fetchAssets();
    } catch (error) {
      toast.error('Error al actualizar activo');
    }
  };

  const handleTransfer = (asset) => {
    setSelectedAsset(asset);
    setTransferForm({ new_store_id: '', transfer_reason: '' });
    setTransferDialogOpen(true);
  };

  const handleSaveTransfer = async () => {
    if (!transferForm.new_store_id || !transferForm.transfer_reason) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/${selectedAsset.id}/transfer`, transferForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Activo transferido correctamente');
      setTransferDialogOpen(false);
      fetchAssets();
    } catch (error) {
      toast.error('Error al transferir activo');
    }
  };

  const handleDeactivate = async (assetId) => {
    if (!window.confirm('¿Estás seguro de dar de baja este activo?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Activo dado de baja correctamente');
      fetchAssets();
    } catch (error) {
      toast.error('Error al dar de baja activo');
    }
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'N/A';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-GT');
  };

  const getConditionBadge = (condition) => {
    const colors = {
      'Nuevo': 'bg-green-100 text-green-800',
      'Usado': 'bg-blue-100 text-blue-800',
      'En reparación': 'bg-yellow-100 text-yellow-800',
      'Dado de baja': 'bg-red-100 text-red-800'
    };
    return colors[condition] || 'bg-gray-100 text-gray-800';
  };

  const filteredAssets = assets.filter(asset => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      asset.name.toLowerCase().includes(search) ||
      (asset.brand && asset.brand.toLowerCase().includes(search)) ||
      (asset.serial_number && asset.serial_number.toLowerCase().includes(search))
    );
  });

  const getTotalValue = () => {
    return filteredAssets.reduce((sum, asset) => sum + (asset.current_value * asset.quantity), 0);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Activos de la Empresa</h1>
          <p className="text-slate-600 mt-1">Gestiona el inventario de activos fijos</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total de Activos</p>
                <p className="text-2xl font-bold text-slate-900">{filteredAssets.length}</p>
              </div>
              <Package className="h-10 w-10 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Valor Total</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(getTotalValue())}</p>
              </div>
              <Package className="h-10 w-10 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Categorías</p>
                <p className="text-2xl font-bold text-slate-900">{categories.length}</p>
              </div>
              <Filter className="h-10 w-10 text-purple-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Tienda</Label>
              <select
                value={filters.store_id}
                onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">Todas las tiendas</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Categoría</Label>
              <div className="flex gap-2">
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="all">Todas las categorías</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <Button
                  onClick={() => setCategoryDialogOpen(true)}
                  className="px-3 whitespace-nowrap bg-purple-600 hover:bg-purple-700 text-white"
                  title="Nueva Categoría"
                >
                  + Nueva
                </Button>
              </div>
            </div>

            <div>
              <Label>Estado</Label>
              <select
                value={filters.condition}
                onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">Todos los estados</option>
                <option value="Nuevo">Nuevo</option>
                <option value="Usado">Usado</option>
                <option value="En reparación">En reparación</option>
              </select>
            </div>

            <div>
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Nombre, marca, serie..."
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-12 text-slate-600">
            Cargando activos...
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No hay activos registrados</p>
            <p className="text-sm text-slate-500 mt-1">
              Crea una transacción y marca "Registrar como activo"
            </p>
          </div>
        ) : (
          filteredAssets.map((asset) => (
            <Card key={asset.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{asset.name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      {asset.brand || 'Sin marca'} • {asset.category}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionBadge(asset.condition)}`}>
                    {asset.condition}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tienda:</span>
                    <span className="font-medium">{getStoreName(asset.store_id)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Cantidad:</span>
                    <span className="font-medium">{asset.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Valor:</span>
                    <span className="font-medium">{formatCurrency(asset.current_value)}</span>
                  </div>
                  {asset.serial_number && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Serie:</span>
                      <span className="font-mono text-xs">{asset.serial_number}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleViewDetail(asset)}
                  >
                    Ver Detalle
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(asset)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleTransfer(asset)}
                  >
                    <ArrowRightLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeactivate(asset.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del Activo</DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Nombre</Label>
                  <p className="font-medium">{selectedAsset.name}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Categoría</Label>
                  <p className="font-medium">{selectedAsset.category}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Marca</Label>
                  <p className="font-medium">{selectedAsset.brand || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Cantidad</Label>
                  <p className="font-medium">{selectedAsset.quantity}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Número de Serie</Label>
                  <p className="font-mono text-sm">{selectedAsset.serial_number || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Estado</Label>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getConditionBadge(selectedAsset.condition)}`}>
                    {selectedAsset.condition}
                  </span>
                </div>
                <div>
                  <Label className="text-slate-600">Tienda</Label>
                  <p className="font-medium">{getStoreName(selectedAsset.store_id)}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Lugar de Compra</Label>
                  <p className="font-medium">{selectedAsset.purchase_location || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Fecha de Compra</Label>
                  <p className="font-medium">{formatDate(selectedAsset.purchase_date)}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Precio de Compra</Label>
                  <p className="font-medium">{formatCurrency(selectedAsset.purchase_price)}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Valor Actual</Label>
                  <p className="font-medium">{formatCurrency(selectedAsset.current_value)}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Garantía</Label>
                  <p className="font-medium">
                    {selectedAsset.warranty_months ? `${selectedAsset.warranty_months} meses` : 'Sin garantía'}
                  </p>
                </div>
                {selectedAsset.warranty_expiry && (
                  <div>
                    <Label className="text-slate-600">Vence Garantía</Label>
                    <p className="font-medium">{formatDate(selectedAsset.warranty_expiry)}</p>
                  </div>
                )}
                {selectedAsset.physical_location && (
                  <div className="col-span-2">
                    <Label className="text-slate-600">Ubicación Física</Label>
                    <p className="font-medium">{selectedAsset.physical_location}</p>
                  </div>
                )}
                {selectedAsset.notes && (
                  <div className="col-span-2">
                    <Label className="text-slate-600">Notas</Label>
                    <p className="text-sm text-slate-700">{selectedAsset.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Activo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Marca</Label>
              <Input
                value={editForm.brand}
                onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
              />
            </div>
            <div>
              <Label>Cantidad</Label>
              <Input
                type="number"
                value={editForm.quantity}
                onChange={(e) => setEditForm({ ...editForm, quantity: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Número de Serie</Label>
              <Input
                value={editForm.serial_number}
                onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <select
                value={editForm.condition}
                onChange={(e) => setEditForm({ ...editForm, condition: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="Nuevo">Nuevo</option>
                <option value="Usado">Usado</option>
                <option value="En reparación">En reparación</option>
              </select>
            </div>
            <div>
              <Label>Ubicación Física</Label>
              <Input
                value={editForm.physical_location}
                onChange={(e) => setEditForm({ ...editForm, physical_location: e.target.value })}
                placeholder="Ej: Bodega A, Estante 3"
              />
            </div>
            <div>
              <Label>Valor Actual</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.current_value}
                onChange={(e) => setEditForm({ ...editForm, current_value: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label>Notas</Label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} className="flex-1">Guardar Cambios</Button>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transferir Activo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedAsset && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Activo:</p>
                <p className="font-medium">{selectedAsset.name}</p>
                <p className="text-sm text-slate-600 mt-1">Tienda actual:</p>
                <p className="font-medium">{getStoreName(selectedAsset.store_id)}</p>
              </div>
            )}
            <div>
              <Label>Nueva Tienda *</Label>
              <select
                value={transferForm.new_store_id}
                onChange={(e) => setTransferForm({ ...transferForm, new_store_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Seleccionar tienda...</option>
                {stores.filter(s => s.id !== selectedAsset?.store_id).map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Motivo de Transferencia *</Label>
              <textarea
                value={transferForm.transfer_reason}
                onChange={(e) => setTransferForm({ ...transferForm, transfer_reason: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Explica el motivo de la transferencia..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveTransfer} className="flex-1">Transferir</Button>
              <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para Nueva Categoría */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Categoría de Activo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div>
              <Label>Nombre de la Categoría *</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Ej: Vehículos, Maquinaria, etc."
                required
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-1">Ingresa un nombre descriptivo para la nueva categoría</p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">Crear Categoría</Button>
              <Button type="button" variant="outline" onClick={() => {
                setCategoryDialogOpen(false);
                setNewCategoryName('');
              }}>Cancelar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assets;
